import { useState, useEffect, useRef } from 'react';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { User, Company, UserCompany } from '../types/index';
import { AuthService } from '../services/authService';
import { buildFunctionUrl } from '../utils/functionsUrl';
import { buildAuthHeaders } from '../utils/authHeaders';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const checkingRef = useRef(false);
  const lastAdminUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // 初回チェック時にlastAdminUserIdRefを初期化
    const localStorage = typeof window !== 'undefined' ? window.localStorage : null;
    if (localStorage) {
      lastAdminUserIdRef.current = localStorage.getItem('adminUserId');
    }
    
    // 1. 最初に管理者ユーザーセッションをチェック
    const checkAdminSession = async () => {
      if (checkingRef.current) {
        return false; // 既にチェック中
      }
      checkingRef.current = true;
      
      const localStorage = typeof window !== 'undefined' ? window.localStorage : null;
      if (localStorage) {
        const adminUserId = localStorage.getItem('adminUserId');
        if (adminUserId) {
          try {
            // Cloud Function を使用してユーザーデータを取得（サーバー側で権限チェック）
            const response = await fetch(
              buildFunctionUrl('getUserDataByDocId'),
              {
                method: 'POST',
                headers: await buildAuthHeaders(),
                body: JSON.stringify({ docId: adminUserId }),
              }
            );

            if (!response.ok) {
              console.warn('[useAuth] Cloud Function エラー:', response.status);
              if (response.status === 404 && localStorage) {
                console.warn('[useAuth] 無効な管理者IDを検出したため、localStorage をクリアします');
                localStorage.removeItem('adminUserId');
              }
              checkingRef.current = false;
              return false;
            }

            const result = await response.json();
            if (result.success && result.data) {
              const userData = result.data;
              const adminUser: User = {
                uid: adminUserId,
                email: userData.email || undefined,
                displayName: userData.displayName || undefined,
                isAnonymous: false,
                userType: userData.userType,
                companyId: userData.companyId,
                companyName: userData.companyName,
                createdAt: userData.createdAt,
                lastLoginAt: userData.lastLoginAt,
                isActive: userData.isActive,
                settings: userData.settings
              };
              setUser(adminUser);
              setLoading(false);
              checkingRef.current = false;
              return true;  // 管理者ユーザーが見つかった
            }
          } catch (err) {
            console.warn('[useAuth] 管理者セッション復元エラー:', err);
            if (localStorage) {
              localStorage.removeItem('adminUserId');
            }
          }
        }
      }
      checkingRef.current = false;
      return false;  // 管理者ユーザーが見つからない
    };

    let unsubscribeAuth: (() => void) | undefined;
    let storageCheckInterval: number | undefined;

    // 2. 管理者セッションをチェック、見つからなければ Firebase Auth をチェック
    checkAdminSession().then((isAdminSession) => {
      if (isAdminSession) {
        // 管理者セッションがあれば、localStorageの変更を監視して再チェック
        storageCheckInterval = setInterval(() => {
          const localStorage = typeof window !== 'undefined' ? window.localStorage : null;
          if (localStorage) {
            const adminUserId = localStorage.getItem('adminUserId');
            if (adminUserId !== lastAdminUserIdRef.current) {
              lastAdminUserIdRef.current = adminUserId;
              if (!adminUserId) {
                // adminUserIdが削除された場合、userをnullに設定
                setUser(null);
                setLoading(false);
              } else {
                // adminUserIdが変更された場合、再チェック
                checkAdminSession();
              }
            }
          }
        }, 500); // 0.5秒ごとにチェック
        return;
      }

      // 管理者セッションがなければ、Firebase Auth を確認
      unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
        try {
          if (firebaseUser) {
            // Firestoreからユーザー情報を取得（Flutter版と同じ方式）
            const userRef = doc(db, 'users', firebaseUser.uid);
            
            try {
              const userDoc = await getDoc(userRef);
              
              if (userDoc.exists()) {
                const userData = userDoc.data();
                const userDataWithFirebase: User = {
                  uid: firebaseUser.uid,
                  email: firebaseUser.email || undefined,
                  displayName: userData.displayName || firebaseUser.displayName || undefined,
                  isAnonymous: firebaseUser.isAnonymous,
                  userType: userData.userType,
                  companyId: userData.companyId,
                  companyName: userData.companyName,
                  createdAt: userData.createdAt,
                  lastLoginAt: userData.lastLoginAt,
                  isActive: userData.isActive,
                  settings: userData.settings
                };
                setUser(userDataWithFirebase);
                
                // デバイス登録は、LoginPage.tsx の決済完了時の自動ログイン時のみ実行される
                // （ログイン時やアプリ起動時の重複登録を防ぐため、useAuthからの自動登録は行わない）
              } else {
                // Firestoreにユーザー情報がない場合は基本的な情報のみ
                const userData: User = {
                  uid: firebaseUser.uid,
                  email: firebaseUser.email || undefined,
                  displayName: firebaseUser.displayName || undefined,
                  isAnonymous: firebaseUser.isAnonymous
                };
                setUser(userData);
              }
            } catch (firestoreError) {
              console.warn('Firestore接続エラー、基本情報のみ設定:', firestoreError);
              // Firestore接続エラーの場合は基本的な情報のみ
              const userData: User = {
                uid: firebaseUser.uid,
                email: firebaseUser.email || undefined,
                displayName: firebaseUser.displayName || undefined,
                isAnonymous: firebaseUser.isAnonymous
              };
              setUser(userData);
            }
          } else {
            // Firebase Authにユーザーがいない場合、localStorageをチェック
            const localStorage = typeof window !== 'undefined' ? window.localStorage : null;
            if (localStorage) {
              const adminUserId = localStorage.getItem('adminUserId');
              if (adminUserId) {
                // localStorageにadminUserIdがある場合、管理者セッションをチェック
                checkAdminSession();
                return;
              }
            }
            setUser(null);
          }
        } catch (error) {
          console.error('認証状態変更エラー:', error);
          setUser(null);
        } finally {
          setLoading(false);
        }
      });

      // localStorageの変更を監視（管理者セッションが追加された場合に再チェック）
      storageCheckInterval = setInterval(() => {
        const localStorage = typeof window !== 'undefined' ? window.localStorage : null;
        if (localStorage) {
          const adminUserId = localStorage.getItem('adminUserId');
          if (adminUserId !== lastAdminUserIdRef.current) {
            lastAdminUserIdRef.current = adminUserId;
            if (adminUserId) {
              // adminUserIdが追加または変更された場合、管理者セッションをチェック
              checkAdminSession();
            } else {
              // adminUserIdが削除された場合、userをnullに設定
              setUser(null);
              setLoading(false);
            }
          }
        }
      }, 500); // 0.5秒ごとにチェック
    });

    return () => {
      if (unsubscribeAuth) {
        unsubscribeAuth();
      }
      if (storageCheckInterval) {
        clearInterval(storageCheckInterval);
      }
      checkingRef.current = false;
    };
  }, []);

  return { user, loading };
};

export const useCompany = (userId: string | null) => {
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribeCompany: (() => void) | undefined;
    let currentCompanyId: string | null = null;

    if (!userId) {
      if (unsubscribeCompany) {
        unsubscribeCompany();
        unsubscribeCompany = undefined;
      }
      setCompany(null);
      setLoading(false);
      return;
    }

    const userRef = doc(db, 'users', userId);
    const unsubscribeUser = onSnapshot(
      userRef,
      (userDoc) => {
        try {
          if (!userDoc.exists()) {
            if (unsubscribeCompany) {
              unsubscribeCompany();
              unsubscribeCompany = undefined;
            }
            currentCompanyId = null;
            setCompany(null);
            setLoading(false);
            return;
          }

          const userData = userDoc.data();
          const companyId = userData.companyId as string | undefined;

          if (!companyId) {
            if (unsubscribeCompany) {
              unsubscribeCompany();
              unsubscribeCompany = undefined;
            }
            currentCompanyId = null;
            setCompany(null);
            setLoading(false);
            return;
          }

          if (companyId === currentCompanyId && unsubscribeCompany) {
            // 既存のリスナーが最新の会社IDを監視しているためそのまま利用
            return;
          }

          if (unsubscribeCompany) {
            unsubscribeCompany();
            unsubscribeCompany = undefined;
          }

          currentCompanyId = companyId;
          setLoading(true);

          const companyRef = doc(db, 'companies', companyId);
          unsubscribeCompany = onSnapshot(
            companyRef,
            (companyDoc) => {
              try {
                if (companyDoc.exists()) {
                  const companyData = { id: companyDoc.id, ...companyDoc.data() } as Company;
                  setCompany(companyData);
                } else {
                  setCompany(null);
                }
              } catch (companyError) {
                console.warn('会社情報取得エラー:', companyError);
                setCompany(null);
              } finally {
                setLoading(false);
              }
            },
            (error) => {
              console.error('会社情報リスナーエラー:', error);
              setCompany(null);
              setLoading(false);
            }
          );
        } catch (error) {
          console.error('ユーザー情報取得エラー:', error);
          setCompany(null);
          setLoading(false);
        }
      },
      (error) => {
        console.error('ユーザー情報リスナーエラー:', error);
        setCompany(null);
        setLoading(false);
      }
    );

    return () => {
      unsubscribeUser();
      if (unsubscribeCompany) {
        unsubscribeCompany();
      }
    };
  }, [userId]);

  return { company, loading };
};

export const useUserCompany = (userId: string | null) => {
  const [userCompany, setUserCompany] = useState<UserCompany | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setUserCompany(null);
      setLoading(false);
      return;
    }

    // usersコレクションから直接ユーザー会社情報を取得（Flutter版と同じ方式）
    const userRef = doc(db, 'users', userId);
    const unsubscribe = onSnapshot(userRef, 
      (userDoc) => {
        try {
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.companyId && userData.companyName) {
              const userCompanyData: UserCompany = {
                id: userId,
                companyId: userData.companyId,
                companyName: userData.companyName,
                isAdmin: userData.userType === 'admin' || userData.userType === 'superadmin',
                deviceId: userData.deviceId || '',
                createdAt: userData.createdAt
              };
              setUserCompany(userCompanyData);
            } else {
              setUserCompany(null);
            }
          } else {
            setUserCompany(null);
          }
        } catch (error) {
          console.error('ユーザー会社情報取得エラー:', error);
          setUserCompany(null);
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error('ユーザー会社情報リスナーエラー:', error);
        setUserCompany(null);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [userId]);

  return { userCompany, loading };
};